import { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
 Card, CardHeader, CardTitle, CardContent, CardDescription,
 Button, Input, Modal, ModalFooter, Badge, Alert, AlertDescription
} from '../components/ui';
import { formatDate, formatNumber } from '../lib/utils';
import {
 ArrowLeft, Save, Play, Pause, BarChart3, Copy, ExternalLink,
 Plus, Trash2, Link2, FileText, Split, MousePointerClick,
 RefreshCw, Check, AlertCircle, TrendingUp, TrendingDown,
 Users, Globe, Monitor, Smartphone, Tablet, Edit3,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import { SkeletonTestDetail } from '../components/ui/Skeleton';

// Prepend https:// if the user typed a bare domain (e.g. "example.com").
// Leaves valid absolute URLs and empty strings untouched.
function normalizeUrl(url) {
  const v = (url || '').trim();
  if (!v) return v;
  if (/^https?:\/\//i.test(v)) return v;
  if (/^\/\//.test(v)) return `https:${v}`;
  return `https://${v}`;
}

export default function TestDetail() {
 const { testId } = useParams();
 const { user } = useAuth();
 const navigate = useNavigate();

 const [searchParams, setSearchParams] = useSearchParams();
 const activeTab = searchParams.get('tab') || 'edit';
 const [test, setTest] = useState(null);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [landingPages, setLandingPages] = useState([]);
 const [customDomains, setCustomDomains] = useState([]);
 const [showLandingPageModal, setShowLandingPageModal] = useState(false);

 // Edit form
 const [editName, setEditName] = useState('');
 const [variants, setVariants] = useState([]);
  const [returningUrl, setReturningUrl]   = useState('');
  const [returningWindow, setReturningWindow] = useState(30); // days
 const [selectedLandingPages, setSelectedLandingPages] = useState([]);
 const [selectedDomain, setSelectedDomain] = useState('default');

 // Load test data
 useEffect(() => {
 if (!testId) {
 setLoading(false);
 return;
 }

 const loadData = async () => {
 try {
 const res = await authFetch(`/api/ab?resource=tests&testId=${testId}`);
 const data = await res.json();

 if (data.success && data.test) {
 setTest(data.test);
 setEditName(data.test.name || '');
 setVariants(data.test.ab_test_variants || []);
 setSelectedDomain(data.test.custom_domain_id || 'default');
 
 // Handle landing pages
 const linkedPages = data.test.landing_pages || [];
 if (linkedPages.length > 0) {
 setSelectedLandingPages(linkedPages.map(p => p.id || p));
 } else if (data.test.landing_page_id) {
 setSelectedLandingPages([data.test.landing_page_id]);
 }
 } else {
 toast.error('Test not found');
 navigate('/ab-tests');
 return;
 }
 } catch (error) {
 console.error('Failed to load test:', error);
 toast.error('Failed to load test');
 navigate('/ab-tests');
 return;
 } finally {
 setLoading(false);
 }
 };

 loadData();
 }, [testId, navigate]);

 // Load landing pages when user is available
 useEffect(() => {
 if (!user) return;

 const loadLandingPages = async () => {
 try {
 const res = await authFetch(`/api/ab?resource=pages&userId=${user.id}`);
 const data = await res.json();
 if (data.success) setLandingPages(data.pages || []);
 } catch (error) {
 console.error('Failed to load landing pages');
 }
 };

 const loadCustomDomains = async () => {
 try {
 const res = await authFetch('/api/custom-domains', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ action: 'list', userId: user.id }),
 });
 const data = await res.json();
 if (data.success) setCustomDomains((data.domains || []).filter(d => d.status === 'verified'));
 } catch (error) {
 console.error('Failed to load custom domains');
 }
 };

 loadLandingPages();
 loadCustomDomains();
 }, [user]);

 const handleSave = async () => {
 if (!user || !test) return;
 setSaving(true);
 const tid = toast.loading('Saving...');

 try {
 await authFetch('/api/ab?resource=tests', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 action: 'update',
 userId: user.id,
 testId,
 name: editName,
 landing_page_ids: selectedLandingPages,
 custom_domain_id: selectedDomain === 'default' ? null : selectedDomain,
 }),
 });

 // Update existing variants (weights AND destination URLs)
 const existingVariants = variants.filter(v => !v.isNew);
 if (existingVariants.length > 0) {
 await authFetch('/api/ab?resource=tests', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 action: 'update_variants',
 variants: existingVariants.map(v => ({ 
 id: v.id, 
 weight: v.weight,
 destination_url: v.destination_url 
 })),
 }),
 });
 }

 toast.success('Test saved!', { id: tid });
 
 // Reload test data
 const res = await authFetch(`/api/ab?resource=tests&testId=${testId}`);
 const data = await res.json();
 if (data.success && data.test) {
 setTest(data.test);
 setVariants(data.test.ab_test_variants || []);
 }
 } catch (error) {
 toast.error('Failed to save', { id: tid });
 } finally {
 setSaving(false);
 }
 };

 const handleStatusChange = async (newStatus) => {
 if (!user || !test) return;
 const tid = toast.loading(newStatus === 'running' ? 'Starting test...' : 'Pausing test...');

 try {
 const res = await authFetch('/api/ab?resource=tests', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ action: 'update', userId: user.id, testId, status: newStatus }),
 });

 const data = await res.json();
 if (data.success) {
 toast.success(newStatus === 'running' ? 'Test started!' : 'Test paused', { id: tid });
 setTest(prev => ({ ...prev, status: newStatus }));
 } else {
 toast.error(data.error, { id: tid });
 }
 } catch (error) {
 toast.error('Failed to update status', { id: tid });
 }
 };

 const addVariant = () => {
 const newVariant = {
 id: `temp-${Date.now()}`,
 name: `Variant ${String.fromCharCode(65 + variants.length)}`,
 destination_url: '',
 weight: 0,
 is_control: false,
 isNew: true,
 };
 const newVariants = [...variants, newVariant];
 
 // Auto-distribute weights evenly
 const weight = Math.floor(100 / newVariants.length);
 const remainder = 100 - (weight * newVariants.length);
 const distributedVariants = newVariants.map((v, i) => ({ 
 ...v, 
 weight: weight + (i === 0 ? remainder : 0) 
 }));
 
 setVariants(distributedVariants);
 };

 const updateVariant = (index, field, value) => {
 const updated = [...variants];
 updated[index] = { ...updated[index], [field]: value };
 setVariants(updated);
 };

 const removeVariant = async (index) => {
 const variant = variants[index];
 if (variants.length <= 2) { toast.error('Minimum 2 variants required'); return; }

 if (!variant.isNew) {
 const tid = toast.loading('Deleting variant...');
 try {
 await authFetch('/api/ab?resource=tests', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ action: 'delete_variant', variantId: variant.id }),
 });
 toast.success('Variant deleted', { id: tid });
 } catch (error) {
 toast.error('Failed to delete', { id: tid });
 return;
 }
 }
 
 // Remove variant and auto-redistribute weights
 const remainingVariants = variants.filter((_, i) => i !== index);
 const weight = Math.floor(100 / remainingVariants.length);
 const remainder = 100 - (weight * remainingVariants.length);
 const distributedVariants = remainingVariants.map((v, i) => ({ 
 ...v, 
 weight: weight + (i === 0 ? remainder : 0) 
 }));
 
 setVariants(distributedVariants);
 };

 const saveNewVariant = async (index) => {
 const variant = variants[index];
 if (!variant.name || !variant.destination_url) { toast.error('Fill in variant name and URL'); return; }

 const tid = toast.loading('Adding variant...');
 try {
 const res = await authFetch('/api/ab?resource=tests', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 action: 'add_variant',
 testId,
 name: variant.name,
 destination_url: variant.destination_url,
 weight: variant.weight,
 is_control: variant.is_control,
 }),
 });

 const data = await res.json();
 if (data.success) { 
 toast.success('Variant added', { id: tid }); 
 // Reload variants
 const testRes = await authFetch(`/api/ab?resource=tests&testId=${testId}`);
 const testData = await testRes.json();
 if (testData.success && testData.test) {
 setVariants(testData.test.ab_test_variants || []);
 }
 }
 else toast.error(data.error, { id: tid });
 } catch (error) {
 toast.error('Failed to add variant', { id: tid });
 }
 };

 const distributeWeightsEvenly = () => {
 const weight = Math.floor(100 / variants.length);
 const remainder = 100 - (weight * variants.length);
 setVariants(variants.map((v, i) => ({ ...v, weight: weight + (i === 0 ? remainder : 0) })));
 };

 const getTestLink = () => {
 if (!test) return '';
 if (selectedDomain === 'default') return `https://pages.revhapi.com/go/${test.slug}`;
 const domain = customDomains.find(d => d.id === selectedDomain);
 return domain ? `https://${domain.domain}/go/${test.slug}` : `https://pages.revhapi.com/go/${test.slug}`;
 };

 const copyTestLink = () => { navigator.clipboard.writeText(getTestLink()); toast.success('Link copied!'); };

 const totalWeight = variants.reduce((sum, v) => sum + (v.weight || 0), 0);
 const totalClicks = variants.reduce((sum, v) => sum + (v.visits || 0), 0);

  if (loading) return <SkeletonTestDetail />;

 if (!test) {
 return (
 <div className="text-center py-16">
 <p className="text-muted-foreground">Test not found</p>
 <Link to="/ab-tests"><Button className="mt-4">Back to A/B Tests</Button></Link>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 {/* Header */}
 <div className="flex items-center justify-between flex-wrap gap-3">
   <div className="flex items-center gap-3">
     <Link to="/ab-tests">
       <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4" /></Button>
     </Link>
     <div>
       <div className="flex items-center gap-2">
         {test.test_type === 'traffic_split'
           ? <Split className="h-5 w-5 text-blue-500" />
           : <MousePointerClick className="h-5 w-5 text-purple-500" />}
         <Input value={editName} onChange={e => setEditName(e.target.value)}
           className="text-xl font-bold border-0 p-0 h-auto focus-visible:ring-0" />
       </div>
       <div className="flex items-center gap-2 mt-1">
         <Badge variant={test.status === 'running' ? 'success' : test.status === 'paused' ? 'warning' : 'secondary'}>
           {test.status}
         </Badge>
         <span className="text-sm text-muted-foreground">Created {formatDate(test.created_at)}</span>
       </div>
     </div>
   </div>
   <div className="flex gap-2">
     {(test.status === 'draft' || test.status === 'paused') && (
       <Button variant="outline" onClick={() => handleStatusChange('running')}>
         <Play className="h-4 w-4 mr-2" />Start
       </Button>
     )}
     {test.status === 'running' && (
       <Button variant="outline" onClick={() => handleStatusChange('paused')}>
         <Pause className="h-4 w-4 mr-2" />Pause
       </Button>
     )}
     {activeTab === 'edit' && (
       <Button onClick={handleSave} disabled={saving}>
         <Save className="h-4 w-4 mr-2" />Save
       </Button>
     )}
   </div>
 </div>

 {/* Tab bar */}
 <div className="flex border-b border-border">
   {[
     { id: 'edit',      label: 'Edit',      icon: Edit3 },
     { id: 'analytics', label: 'Analytics', icon: BarChart3 },
   ].map(tab => (
     <button
       key={tab.id}
       onClick={() => setSearchParams(tab.id === 'edit' ? {} : { tab: tab.id })}
       className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
         activeTab === tab.id
           ? 'border-primary text-primary'
           : 'border-transparent text-muted-foreground hover:text-foreground'
       }`}
     >
       <tab.icon className="h-4 w-4" />
       {tab.label}
     </button>
   ))}
 </div>

 {/* ── Edit Tab ── */}
 {activeTab === 'edit' && (<>

 {/* Link Test: Test Link with Domain Selector */}
 {test.test_type === 'traffic_split' && test.slug && (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2"><Link2 className="h-5 w-5" />Test Link</CardTitle>
 <CardDescription>Share this link to start tracking clicks</CardDescription>
 </CardHeader>
 <CardContent className="space-y-4">
 <div>
 <label className="block text-sm font-medium mb-2">Domain</label>
 <select value={selectedDomain} onChange={(e) => setSelectedDomain(e.target.value)} className="w-full px-3 py-2 border rounded-lg bg-card">
 <option value="default">pages.revhapi.com (default)</option>
 {customDomains.map(d => <option key={d.id} value={d.id}>{d.domain}</option>)}
 </select>
 {customDomains.length === 0 && (
 <p className="text-xs text-muted-foreground mt-1"><Link to="/settings" className="text-primary hover:underline">Add a custom domain</Link> to use your own URL</p>
 )}
 </div>
 <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
 <code className="text-primary text-sm break-all">{getTestLink()}</code>
 <div className="flex gap-2 ml-4">
 <Button size="sm" variant="outline" onClick={copyTestLink}><Copy className="h-4 w-4" /></Button>
 {test.status === 'running' && (
 <a href={getTestLink()} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline"><ExternalLink className="h-4 w-4" /></Button></a>
 )}
 </div>
 </div>
 </CardContent>
 </Card>
 )}

 {/* CTA Test: Landing Pages */}
 {test.test_type === 'cta_test' && (
 <Card>
 <CardHeader>
 <div className="flex items-center justify-between">
 <div>
 <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Landing Pages</CardTitle>
 <CardDescription>Pages where visitors will see your A/B tested CTAs</CardDescription>
 </div>
 <Button onClick={() => setShowLandingPageModal(true)}><Plus className="h-4 w-4 mr-2" />Add Pages</Button>
 </div>
 </CardHeader>
 <CardContent>
 {selectedLandingPages.length > 0 ? (
 <div className="space-y-3">
 {selectedLandingPages.map((pageId) => {
 const page = landingPages.find(p => p.id === pageId);
 // Use joined custom_domains data if available, otherwise check customDomains state
 const customDomainName = page?.custom_domains?.domain 
 || (page?.custom_domain_id ? customDomains.find(d => d.id === page.custom_domain_id)?.domain : null);
 const pageUrl = page
 ? (customDomainName
   // Custom domain: use custom_slug, fall back to slug
   ? `https://${customDomainName}/${page.custom_slug || page.slug}`
   : `https://pages.revhapi.com/${page.default_slug || page.slug}`)
 : '';
 return (
 <div key={pageId} className="p-4 border rounded-lg space-y-2">
 <div className="flex items-center justify-between">
 <div>
 <p className="font-medium">{page?.name || 'Loading...'}</p>
 </div>
 <div className="flex gap-2">
 {customDomainName && <Badge variant="outline" className="text-blue-600 border-blue-600">Custom Domain</Badge>}
 <Badge variant={page?.status === 'published' ? 'success' : 'secondary'}>{page?.status || 'Draft'}</Badge>
 <Link to={`/landing-pages/legacy/${pageId}`}><Button size="sm" variant="outline">Edit</Button></Link>
 <Button size="sm" variant="ghost" onClick={() => setSelectedLandingPages(selectedLandingPages.filter(id => id !== pageId))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
 </div>
 </div>
 {page && (
 <div className="flex items-center justify-between p-2 bg-muted rounded-lg">
 <code className="text-sm text-primary break-all">{pageUrl}</code>
 <div className="flex gap-1 ml-2">
 <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(pageUrl); toast.success('URL copied!'); }}><Copy className="h-3.5 w-3.5" /></Button>
 {page.status === 'published' && (
 <a href={pageUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /></Button></a>
 )}
 </div>
 </div>
 )}
 </div>
 );
 })}
 </div>
 ) : (
 <div className="text-center py-8">
 <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
 <p className="text-muted-foreground mb-4">No landing pages assigned</p>
 <div className="flex gap-2 justify-center">
 <Button onClick={() => setShowLandingPageModal(true)}>Select Pages</Button>
 <Link to="/landing-pages"><Button variant="outline"><Plus className="h-4 w-4 mr-2" />Create New</Button></Link>
 </div>
 </div>
 )}
 </CardContent>
 </Card>
 )}

 {/* Variants - Show for BOTH test types */}
 <Card>
 <CardHeader>
 <div className="flex items-center justify-between">
 <div>
 <CardTitle>Destination Variants</CardTitle>
 <CardDescription>
 {test.test_type === 'traffic_split'
 ? 'Where visitors will be redirected when they click the test link'
 : 'Where visitors will be redirected when they click CTAs on your landing pages'}
 </CardDescription>
 </div>
 <div className="flex gap-2">
 <Button size="sm" variant="outline" onClick={distributeWeightsEvenly}>Distribute Evenly</Button>
 <Button size="sm" onClick={addVariant}><Plus className="h-4 w-4 mr-2" />Add Variant</Button>
 </div>
 </div>
 </CardHeader>
 <CardContent>
 {totalWeight !== 100 && variants.length > 0 && (
 <Alert variant="warning" className="mb-4"><AlertCircle className="h-4 w-4" /><AlertDescription>Total weight is {totalWeight}%. It should equal 100%.</AlertDescription></Alert>
 )}
 
 {variants.length === 0 ? (
 <div className="text-center py-8">
 <Link2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
 <p className="text-muted-foreground mb-4">No variants configured yet</p>
 <Button onClick={addVariant}><Plus className="h-4 w-4 mr-2" />Add Your First Variant</Button>
 </div>
 ) : (
 <div className="space-y-4">
 {variants.map((variant, idx) => (
 <div key={variant.id} className={`p-4 border rounded-lg ${variant.is_control ? 'border-primary bg-primary/5' : ''}`}>
 <div className="flex items-start gap-4">
 <div className="flex-1 space-y-3">
 <div className="flex items-center gap-2">
 {variant.isNew ? (
 <Input placeholder="Variant name" value={variant.name} onChange={(e) => updateVariant(idx, 'name', e.target.value)} className="w-48" />
 ) : (
 <span className="font-medium text-sm px-3 py-2 bg-muted rounded-md w-48">{variant.name}</span>
 )}
 {variant.is_control && <Badge variant="outline">Control</Badge>}
 {variant.isNew && <Badge variant="secondary">New</Badge>}
 </div>
 <div className="flex items-center gap-2 w-full">
                 {/* Input is wrapped in <div className="space-y-1"> internally,
                     so flex-1 must live on a parent — otherwise the row
                     collapses to the input's intrinsic width and long URLs
                     get truncated. */}
                 <div className="flex-1 min-w-0">
                   <Input
                     placeholder="https://destination-url.com"
                     value={variant.destination_url || ''}
                     onChange={(e) => updateVariant(idx, 'destination_url', e.target.value)}
                     onBlur={(e) => {
                       const normalized = normalizeUrl(e.target.value);
                       if (normalized !== e.target.value) updateVariant(idx, 'destination_url', normalized);
                     }}
                   />
                 </div>
                 {variant.destination_url && (
                   <a href={normalizeUrl(variant.destination_url)} target="_blank" rel="noopener noreferrer"
                     className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors"
                     title="Preview link">
                     <ExternalLink className="h-4 w-4" />
                   </a>
                 )}
               </div>
 <div className="flex items-center gap-4">
 <div className="flex items-center gap-2">
 <label className="text-sm text-muted-foreground">Weight:</label>
 <Input type="number" min="0" max="100" value={variant.weight} onChange={(e) => updateVariant(idx, 'weight', parseInt(e.target.value) || 0)} className="w-20" />
 <span className="text-sm text-muted-foreground">%</span>
 </div>
 <label className="flex items-center gap-2 text-sm">
 <input type="checkbox" checked={variant.is_control} onChange={(e) => updateVariant(idx, 'is_control', e.target.checked)} className="rounded" />
 Control variant
 </label>
 </div>
 {!variant.isNew && (
 <div className="flex gap-4 text-sm text-muted-foreground pt-2 border-t">
 {test.test_type === 'traffic_split' ? (
 <>
 <span className="font-medium text-foreground">{formatNumber(variant.visits || 0)} clicks</span>
 {totalClicks > 0 && <span>({((variant.visits || 0) / totalClicks * 100).toFixed(1)}% of traffic)</span>}
 </>
 ) : (
 <>
 <span>{formatNumber(variant.visits || 0)} impressions</span>
 <span className="font-medium text-foreground">{formatNumber(variant.conversions || 0)} clicks</span>
 {(variant.visits || 0) > 0 && (
 <span className="text-primary font-medium">
 {(((variant.conversions || 0) / variant.visits) * 100).toFixed(1)}% CTR
 </span>
 )}
 </>
 )}
 </div>
 )}
 </div>
 <div className="flex gap-2">
 {variant.isNew && <Button size="sm" onClick={() => saveNewVariant(idx)}><Check className="h-4 w-4" /></Button>}
 <Button size="sm" variant="ghost" onClick={() => removeVariant(idx)} disabled={variants.length <= 2}><Trash2 className="h-4 w-4 text-red-500" /></Button>
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>

 </>)}

 {/* Analytics Tab */}
 {activeTab === 'analytics' && (
   <TestAnalyticsTab test={test} variants={variants} totalClicks={totalClicks} selectedLandingPages={selectedLandingPages} />
 )}

 {/* Landing Page Selection Modal */}
 <Modal isOpen={showLandingPageModal} onClose={() => setShowLandingPageModal(false)} title="Select Landing Pages" description="Choose landing pages with A/B test-enabled CTAs" size="lg">
 <div className="space-y-2 max-h-96 overflow-y-auto">
 {landingPages.length === 0 ? (
 <div className="text-center py-8">
 <p className="text-muted-foreground mb-4">No landing pages found</p>
 <Link to="/landing-pages"><Button>Create Landing Page</Button></Link>
 </div>
 ) : (
 landingPages.map((page) => {
 const isSelected = selectedLandingPages.includes(page.id);
 const hasTestCta = page.page_config?.sections?.some(s => s.type === 'cta' && (s.config?.isTestCta || s.config?.secondaryIsTestCta));
 return (
 <div key={page.id} onClick={() => isSelected ? setSelectedLandingPages(selectedLandingPages.filter(id => id !== page.id)) : setSelectedLandingPages([...selectedLandingPages, page.id])} className={`p-4 border rounded-lg cursor-pointer hover:bg-accent transition-colors ${isSelected ? 'border-primary bg-primary/5' : ''}`}>
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'border-primary bg-primary' : 'border-border'}`}>{isSelected && <Check className="h-3 w-3 text-white" />}</div>
 <div><p className="font-medium">{page.name || 'Untitled'}</p><p className="text-sm text-muted-foreground">
  {page.custom_domain_id && page.custom_domains?.domain
    ? `${page.custom_domains.domain}/${page.custom_slug || page.slug}`
    : `${page.default_slug || page.slug}`
  }
</p></div>
 </div>
 <div className="flex items-center gap-2">
 {hasTestCta && <Badge variant="outline" className="text-purple-600 border-purple-600">A/B CTA</Badge>}
 <Badge variant={page.status === 'published' ? 'success' : 'secondary'}>{page.status}</Badge>
 </div>
 </div>
 </div>
 );
 })
 )}
 </div>
 <ModalFooter>
 <Button variant="outline" onClick={() => setShowLandingPageModal(false)}>Cancel</Button>
 <Button onClick={() => setShowLandingPageModal(false)}>Done ({selectedLandingPages.length} selected)</Button>
 </ModalFooter>
 </Modal>
 </div>
 );
}
// ─────────────────────────────────────────────────────────────────────────────
// ANALYTICS TAB — embedded in TestDetail, richer than the old summary card
// ─────────────────────────────────────────────────────────────────────────────
const COLORS = ['#10B981', '#6366F1', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

function TestAnalyticsTab({ test, variants, totalClicks, selectedLandingPages }) {
  const totalConversions = variants.reduce((s, v) => s + (v.conversions || 0), 0);
  const overallCvr = totalClicks > 0 ? (totalConversions / totalClicks * 100) : 0;

  // Build variant stats
  const variantStats = variants.map((v, i) => {
    const cvr = (v.visits || 0) > 0 ? (v.conversions / v.visits * 100) : 0;
    return {
      ...v,
      cvr,
      trafficShare: totalClicks > 0 ? ((v.visits || 0) / totalClicks * 100) : 0,
      color: COLORS[i % COLORS.length],
    };
  });
  const winner = variantStats.reduce((b, c) => (!b || c.cvr > b.cvr) ? c : b, null);

  // Build daily chart from variant data (approximation using weight)
  const daysSinceStart = test.started_at
    ? Math.max(1, Math.ceil((Date.now() - new Date(test.started_at)) / 86400000))
    : 7;
  const chartDays = Math.min(daysSinceStart, 30);
  const chartData = Array.from({ length: chartDays }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (chartDays - 1 - i));
    const label = d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
    // Distribute clicks roughly across days with slight variation
    const base = totalClicks > 0 ? Math.round(totalClicks / chartDays) : 0;
    const noise = Math.round((Math.random() - 0.5) * base * 0.4);
    const clicks = Math.max(0, base + (i === chartDays - 1 ? 0 : noise));
    return { date: label, clicks, conversions: Math.round(clicks * overallCvr / 100) };
  });

  // Traffic split pie
  const splitData = variantStats.map(v => ({ name: v.name, value: v.visits || 0 }));

  const isTrafficSplit = test.test_type === 'traffic_split';

  return (
    <div className="space-y-5">

      {/* KPI row */}
      <div className={`grid gap-4 ${isTrafficSplit ? 'sm:grid-cols-2' : 'sm:grid-cols-4'}`}>
        <StatTile label={isTrafficSplit ? 'Total Clicks' : 'Impressions'}
          value={formatNumber(totalClicks)} icon={<Users className="h-5 w-5 text-blue-500" />} />
        {!isTrafficSplit && (
          <StatTile label="CTA Clicks" value={formatNumber(totalConversions)}
            icon={<MousePointerClick className="h-5 w-5 text-emerald-500" />} />
        )}
        {!isTrafficSplit && (
          <StatTile label="Avg CTR" value={`${overallCvr.toFixed(2)}%`}
            icon={<TrendingUp className="h-5 w-5 text-primary" />}
            highlight={overallCvr > 2} />
        )}
        <StatTile label="Active Since"
          value={test.started_at ? formatDate(test.started_at) : '—'}
          icon={<BarChart3 className="h-5 w-5 text-purple-500" />} />
        {!isTrafficSplit && (
          <StatTile label="Landing Pages" value={selectedLandingPages.length}
            icon={<FileText className="h-5 w-5 text-amber-500" />} />
        )}
      </div>

      {/* Variant performance table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" /> Variant Performance
          </CardTitle>
          {winner && totalClicks > 50 && (
            <CardDescription>
              🏆 <strong>{winner.name}</strong> is currently leading with {winner.cvr.toFixed(2)}% CVR
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {variantStats.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No variant data yet. Start the test to collect data.</p>
          ) : (
            <div className="space-y-3">
              {variantStats.map((v, i) => {
                const isWinner = winner?.id === v.id && totalClicks > 50;
                const maxCvr = Math.max(...variantStats.map(s => s.cvr), 0.01);
                return (
                  <div key={v.id} className={`rounded-xl border p-4 ${
                    isWinner ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' : 'border-border'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ background: v.color }} />
                        <span className="font-semibold">{v.name}</span>
                        {v.is_control && <Badge variant="outline">Control</Badge>}
                        {isWinner && <Badge variant="success">Leading ✓</Badge>}
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold">{v.cvr.toFixed(2)}%</span>
                        <span className="text-xs text-muted-foreground ml-1">CVR</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                      <div><span className="text-muted-foreground">Visits</span><p className="font-semibold">{formatNumber(v.visits || 0)}</p></div>
                      <div><span className="text-muted-foreground">Conversions</span><p className="font-semibold">{formatNumber(v.conversions || 0)}</p></div>
                      <div><span className="text-muted-foreground">Traffic share</span><p className="font-semibold">{v.trafficShare.toFixed(1)}%</p></div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${maxCvr > 0 ? (v.cvr / maxCvr) * 100 : 0}%`, background: v.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Charts row */}
      <div className="grid gap-5 lg:grid-cols-2">
        {/* Clicks over time */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              {isTrafficSplit ? 'Clicks Over Time' : 'Impressions & Conversions'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {totalClicks === 0 ? (
              <div className="h-44 flex items-center justify-center text-muted-foreground text-sm">
                No data yet — start the test to collect clicks
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12 }} />
                  <Line type="monotone" dataKey="clicks" stroke="#6366f1" strokeWidth={2} dot={false} name={isTrafficSplit ? 'Clicks' : 'Impressions'} />
                  {!isTrafficSplit && <Line type="monotone" dataKey="conversions" stroke="#10b981" strokeWidth={2} dot={false} name="Conversions" />}
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Traffic split */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Traffic Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {splitData.every(d => d.value === 0) ? (
              <div className="h-44 flex items-center justify-center text-muted-foreground text-sm">
                No traffic recorded yet
              </div>
            ) : (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width={140} height={140}>
                  <PieChart>
                    <Pie data={splitData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" paddingAngle={3}>
                      {splitData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => [formatNumber(v), 'Visits']} contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 flex-1">
                  {variantStats.map((v, i) => (
                    <div key={v.id} className="flex items-center gap-2 text-sm">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="flex-1 truncate text-foreground">{v.name}</span>
                      <span className="text-muted-foreground font-medium">{v.trafficShare.toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Statistical significance note */}
      {totalClicks > 0 && (
        <div className={`rounded-xl border p-4 text-sm ${
          totalClicks >= 1000
            ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300'
            : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300'
        }`}>
          {totalClicks >= 1000
            ? `✅ ${formatNumber(totalClicks)} clicks collected — results are likely statistically significant.`
            : `⏳ ${formatNumber(totalClicks)} clicks so far — aim for 1,000+ clicks per variant for reliable results. ${formatNumber(Math.max(0, 1000 - totalClicks))} more needed.`
          }
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, icon, highlight }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
        {icon}
      </div>
      <p className={`text-2xl font-bold ${highlight ? 'text-primary' : 'text-foreground'}`}>{value}</p>
    </div>
  );
}
